import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer, Box, Typography, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Chip,
  Button, Collapse,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import CategoryIcon from '@mui/icons-material/Category';
import SwapVerticalCircleIcon from '@mui/icons-material/SwapVerticalCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentsIcon from '@mui/icons-material/Payments';
import BadgeIcon from '@mui/icons-material/Badge';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PointOfSaleTwoToneIcon from '@mui/icons-material/PointOfSaleTwoTone';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import LogoHolaVeciLocal from '../marca/LogoHolaVeciLocal';

// Cada item declara si aplica a una sucursal específica (`operativo: true`)
// o si es agregado / administrativo (por defecto). En Modo "Todas las
// sucursales" ocultamos los operativos porque no tienen sentido sin
// una sucursal seleccionada. En Modo Sucursal se muestran todos los
// que respeten permisos.
const menuItems = [
  { to: '/dashboard',     label: 'Dashboard',     icon: <DashboardIcon />,            perm: 'dashboard.view' },
  { to: '/pos',           label: 'Punto de venta', icon: <PointOfSaleIcon />,         perm: 'pos.use',                operativo: true },
  // 'Órdenes pendientes' solo aplica en modo 'vendedor a caja' (mesero arma /
  // cajero cobra), típico de restaurantes o negocios con separación de roles.
  // En tiendas de barrio con una sola persona atendiendo no aplica. Se oculta
  // del sidebar; la ruta y la página quedan intactas para reactivarlo cuando
  // se soporten negocios en modo vendedor_a_caja.
  { to: '/ordenes',       label: 'Órdenes pendientes', icon: <PaymentsIcon />,        perm: 'ordenes.recibir',        operativo: true, badge: 'ordenes', oculto: true },
  { to: '/inventario',    label: 'Inventario',    icon: <Inventory2Icon />,           perm: 'inventario.gestionar' },
  { to: '/movimientos',   label: 'Movimientos',   icon: <SwapVerticalCircleIcon />,   perm: 'inventario.gestionar' },
  { to: '/listas-precios',label: 'Listas de precios', icon: <LocalOfferIcon />,       perm: 'inventario.gestionar' },
  { to: '/categorias',    label: 'Categorías',    icon: <CategoryIcon />,             perm: 'inventario.gestionar' },
  { to: '/clientes',      label: 'Clientes',      icon: <PeopleIcon />,               perm: 'clientes.gestionar' },
  { to: '/ventas',        label: 'Ventas',        icon: <ReceiptLongIcon />,          perm: 'ventas.ver' },
  { to: '/caja',          label: 'Caja',          icon: <AccountBalanceWalletIcon />, perm: 'caja.gestionar',         operativo: true },
  { to: '/reportes',      label: 'Reportes',      icon: <AnalyticsIcon />,            perm: 'reportes.ver' },
  { collapsibleGroup: 'admin', label: 'Administración' },
  { to: '/sucursales',    label: 'Sucursales',    icon: <StorefrontIcon />,           perm: 'usuarios.gestionar', group: 'admin' },
  { to: '/cajas',         label: 'Cajas',         icon: <PointOfSaleTwoToneIcon />,   perm: 'usuarios.gestionar', group: 'admin' },
  { to: '/usuarios',      label: 'Usuarios',      icon: <BadgeIcon />,                perm: 'usuarios.gestionar', group: 'admin' },
  { to: '/domiciliarios', label: 'Domiciliarios', icon: <LocalShippingIcon />,        perm: 'usuarios.gestionar', group: 'admin' },
  { to: '/roles',         label: 'Roles y permisos', icon: <AdminPanelSettingsIcon />, perm: 'usuarios.gestionar', group: 'admin' },
  // La Configuración de la tienda debe estar disponible para el tendero (Admin)
  // y para Superadmin. Ambos tienen 'usuarios.gestionar'.
  { to: '/configuracion', label: 'Configuración', icon: <SettingsIcon />,             perm: 'usuarios.gestionar', group: 'admin' },
  // D-24: cuentas de cobro de la suscripción (solo el negocio; el superadmin las ve en la web admin)
  { to: '/cuentas-cobro', label: 'Mis cuentas de cobro', icon: <ReceiptLongIcon />,  perm: 'usuarios.gestionar', group: 'admin', soloNegocio: true },
  // Grupo Superadmin (solo visible para SUPERADMIN — se filtra abajo)
  { collapsibleGroup: 'superadmin', label: 'Superadmin', soloSuperadmin: true },
  { to: '/superadmin', label: 'Consola plataforma', icon: <BusinessIcon />, perm: 'dashboard.view', group: 'superadmin', soloSuperadmin: true },
];

const Sidebar = ({ drawerWidth, mobileOpen, onClose, isMobile }) => {
  const { user, hasPermission, logout } = useAuth();
  const esSuperadmin = user?.entidadTipo === 'SUPERADMIN';
  const {
    empresa, sucursales, sucursalActiva, esModoTodas, hayVariasSucursales,
    cargando: cargandoEmpresa, errorCarga,
  } = useApp();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const year = new Date().getFullYear();

  // ── Grupos colapsables ────────────────────────────────────────────
  // Se detecta si la ruta actual pertenece a un grupo para auto-expandirlo.
  // Si el usuario navega fuera del grupo (a Dashboard, POS, etc.), el
  // grupo se colapsa automáticamente para recuperar espacio en el sidebar.
  const rutasPorGrupo = useMemo(() => {
    const map = {};
    menuItems.forEach(it => {
      if (it.group && it.to) {
        if (!map[it.group]) map[it.group] = new Set();
        map[it.group].add(it.to);
      }
    });
    return map;
  }, []);

  const grupoActivo = useMemo(() => {
    for (const [g, rutas] of Object.entries(rutasPorGrupo)) {
      if (rutas.has(location.pathname)) return g;
    }
    return null;
  }, [location.pathname, rutasPorGrupo]);

  const [gruposAbiertos, setGruposAbiertos] = useState({});

  // Cuando cambia la ruta, sincronizamos: si la ruta pertenece a un grupo,
  // se abre. Si no, se cierran todos.
  useEffect(() => {
    setGruposAbiertos(prev => {
      const nuevo = {};
      Object.keys(rutasPorGrupo).forEach(g => {
        nuevo[g] = g === grupoActivo;
      });
      return nuevo;
    });
  }, [grupoActivo, rutasPorGrupo]);

  const toggleGrupo = (grupo) => {
    setGruposAbiertos(prev => ({ ...prev, [grupo]: !prev[grupo] }));
  };

  // Nombre de la empresa activa para el header del sidebar.
  const nombreEmpresa =
    empresa?.nombreComercial || empresa?.NombreComercial ||
    empresa?.razonSocial     || empresa?.RazonSocial     ||
    (cargandoEmpresa ? 'Cargando…' :
     errorCarga     ? 'Error al cargar' :
                      'Sin empresa seleccionada');
  const codigoEmpresa = empresa?.codigo || empresa?.Codigo;

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2.5 }}>
        <LogoHolaVeciLocal size={22} compacto />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          v1.0 · Colombia
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ px: 2.5, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>EMPRESA</Typography>
        <Typography variant="body2" fontWeight={600} noWrap title={nombreEmpresa}>
          {nombreEmpresa}
        </Typography>
        {codigoEmpresa && (
          <Chip
            size="small"
            label={codigoEmpresa}
            variant="outlined"
            sx={{ mt: 0.5, height: 20, fontSize: 10 }}
          />
        )}

        {/* Sucursal en operación */}
        {hayVariasSucursales && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>OPERANDO</Typography>
            {esModoTodas ? (
              <Chip
                size="small" label="Todas las sucursales"
                color="primary" variant="filled"
                sx={{ mt: 0.5, height: 22, fontSize: 11 }}
              />
            ) : sucursalActiva ? (
              <Typography variant="body2" fontWeight={500} noWrap sx={{ mt: 0.5 }}>
                {sucursalActiva.nombre || sucursalActiva.Nombre}
              </Typography>
            ) : null}
          </Box>
        )}
      </Box>
      <Divider />
      <List sx={{ flexGrow: 1, py: 1 }}>
        {(() => {
          // Recorremos linealmente los items, pero cuando aparece un
          // 'collapsibleGroup' consumimos todos los items siguientes cuyo
          // 'group' coincida y los envolvemos en un <Collapse>. El grupo
          // termina cuando aparece otro 'collapsibleGroup' o cuando ya
          // no hay más items.
          const nodos = [];
          let i = 0;
          while (i < menuItems.length) {
            const item = menuItems[i];

            // ── Grupo colapsable ────────────────────────────────
            if (item.collapsibleGroup) {
              const grupo = item.collapsibleGroup;
              // Grupo marcado soloSuperadmin y no es superadmin → saltar
              if (item.soloSuperadmin && !esSuperadmin) {
                // saltar hasta el próximo collapsibleGroup
                let j = i + 1;
                while (j < menuItems.length && !menuItems[j].collapsibleGroup) j++;
                i = j; continue;
              }
              const abierto = !!gruposAbiertos[grupo];

              // Recolectar items del grupo hasta el siguiente collapsibleGroup
              const hijos = [];
              let j = i + 1;
              while (j < menuItems.length && !menuItems[j].collapsibleGroup) {
                if (menuItems[j].group === grupo) hijos.push(menuItems[j]);
                j++;
              }

              // Ocultar el grupo completo si ninguno de los hijos pasa
              // los filtros de permiso/visibilidad.
              const hijosVisibles = hijos.filter(h =>
                hasPermission(h.perm) && !h.oculto &&
                !(esModoTodas && h.operativo) &&
                !(h.soloSuperadmin && !esSuperadmin) &&
                !(h.soloNegocio && user?.entidadTipo !== 'NEGOCIO'));
              if (hijosVisibles.length === 0) { i = j; continue; }

              nodos.push(
                <ListItemButton key={`grp-${grupo}`} onClick={() => toggleGrupo(grupo)}
                                sx={{ px: 2.5, py: 0.75, mx: 1.5, borderRadius: 2 }}>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: 'caption', fontWeight: 600,
                      color: 'text.secondary', letterSpacing: 0.3,
                      textTransform: 'uppercase',
                    }} />
                  {abierto ? <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                           : <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                </ListItemButton>
              );

              nodos.push(
                <Collapse key={`col-${grupo}`} in={abierto} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {hijosVisibles.map(h => {
                      const active = location.pathname === h.to;
                      return (
                        <ListItem key={h.to} disablePadding sx={{ px: 1.5, py: 0.25 }}>
                          <ListItemButton
                            component={NavLink} to={h.to}
                            onClick={isMobile ? onClose : undefined}
                            sx={{
                              borderRadius: 2,
                              bgcolor: active ? 'primary.main' : 'transparent',
                              color: active ? '#fff' : 'text.primary',
                              '&:hover': { bgcolor: active ? 'primary.dark' : 'action.hover' },
                            }}>
                            <ListItemIcon sx={{ color: active ? '#fff' : 'text.secondary', minWidth: 40 }}>
                              {h.icon}
                            </ListItemIcon>
                            <ListItemText primary={h.label}
                                          primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 600 : 500 }} />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              );

              i = j;
              continue;
            }

            // ── Item normal (no colapsable) ────────────────────
            if (!hasPermission(item.perm)) { i++; continue; }
            if (item.oculto) { i++; continue; }
            if (esModoTodas && item.operativo) { i++; continue; }
            if (item.soloSuperadmin && !esSuperadmin) { i++; continue; }
            if (item.soloNegocio && user?.entidadTipo !== 'NEGOCIO') { i++; continue; }

            const active = location.pathname === item.to;
            nodos.push(
              <ListItem key={item.to} disablePadding sx={{ px: 1.5, py: 0.25 }}>
                <ListItemButton
                  component={NavLink} to={item.to}
                  onClick={isMobile ? onClose : undefined}
                  sx={{
                    borderRadius: 2,
                    bgcolor: active ? 'primary.main' : 'transparent',
                    color: active ? '#fff' : 'text.primary',
                    '&:hover': { bgcolor: active ? 'primary.dark' : 'action.hover' },
                  }}>
                  <ListItemIcon sx={{ color: active ? '#fff' : 'text.secondary', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label}
                                primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 600 : 500 }} />
                </ListItemButton>
              </ListItem>
            );
            i++;
          }
          return nodos;
        })()}
      </List>
      <Box sx={{
        p: 2, mt: 'auto', bgcolor: 'background.default',
        borderTop: '1px solid', borderColor: 'divider',
      }}>
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{ mb: 1.5, justifyContent: 'flex-start', textTransform: 'none' }}
        >
          Cerrar sesión
        </Button>
        <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', textAlign: 'center', lineHeight: 1.5 }}>
          HolaVeci Local v1.0<br />
          © {year} · Powered by <b>Redeprog</b>
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' },
        }}
      >
        {content}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
        open
      >
        {content}
      </Drawer>
    </>
  );
};

export default Sidebar;
